import { useState } from "react";

import { AlertTriangle, ExternalLink, ShieldAlert } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { useCreatePayloadIndex, useRagAdminConfig, useRagOverview } from "../api";

/**
 * Manage Qdrant — gated on `code_rag.admin`, which no category grant confers.
 *
 * Two halves: a read-only summary of how the collection is configured with a
 * repair action for missing payload indexes, and the embedded Qdrant console.
 *
 * The console is reached at a same-origin path rather than Qdrant's own host.
 * That is not cosmetic: Qdrant serves its dashboard with `X-Frame-Options:
 * DENY` over plain HTTP, so a direct iframe from an HTTPS page fails twice
 * over — blocked as mixed content, and refused as a frame. The nginx location
 * in frontend/nginx.conf proxies it and strips the header.
 */
export function ManageQdrantTab() {
  const { data, isPending, error } = useRagAdminConfig(true);
  const overview = useRagOverview();
  const createIndex = useCreatePayloadIndex();
  const [repaired, setRepaired] = useState<string[]>([]);

  if (isPending) return <Skeleton className="h-96" />;

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn&rsquo;t read the collection config</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  const missing = data.missing_indexes.filter((f) => !repaired.includes(f));

  return (
    <div className="flex flex-col gap-6">
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>The Qdrant console can modify the shared index</AlertTitle>
        <AlertDescription>
          The console linked below is the real thing, and it can delete points or
          drop the collection outright. Rebuilding{" "}
          {overview.data ? overview.data.health.points.toLocaleString() : "29,000"}{" "}
          chunks means re-embedding every repo on the Spark GPU — hours, not
          minutes. Treat it as read-mostly.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Collection</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Name</span>
              <span className="font-mono">{data.collection}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Qdrant</span>
              <span className="break-all font-mono text-xs">{data.qdrant_url}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Embedding model</span>
              <span className="font-mono text-xs">{data.embed_model}</span>
            </div>
            {overview.data ? (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Vectors</span>
                <span className="tabular-nums">
                  {overview.data.health.indexed_vectors.toLocaleString()} ·{" "}
                  {overview.data.health.vector_size}d{" "}
                  {overview.data.health.distance}
                </span>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Payload indexes</CardTitle>
            <p className="text-sm text-muted-foreground">
              Without these, per-repo counts and filtered search fall back to full
              scans — or fail outright, since Qdrant refuses to facet an unindexed
              field.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(data.payload_schema).map(([field, spec]) => (
                <Badge key={field} variant="secondary" className="font-mono text-xs">
                  {field}
                  <span className="ml-1 opacity-60">
                    {typeof spec === "object" && spec !== null && "data_type" in spec
                      ? String((spec as { data_type: unknown }).data_type)
                      : ""}
                  </span>
                </Badge>
              ))}
            </div>

            {missing.length > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-warning">
                  Missing: {missing.join(", ")}
                </p>
                {missing.map((field) => (
                  <Button
                    key={field}
                    size="sm"
                    variant="outline"
                    disabled={createIndex.isPending}
                    onClick={() =>
                      createIndex.mutate(field, {
                        onSuccess: () => {
                          setRepaired((prev) => [...prev, field]);
                          void overview.refetch();
                        },
                      })
                    }
                  >
                    Create index on {field}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-success">
                Every field the feature filters on is indexed.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Qdrant console</CardTitle>
          <p className="text-sm text-muted-foreground">
            Opens at Qdrant&rsquo;s own address rather than embedded here. Its
            console builds every API call from{" "}
            <code className="font-mono text-xs">location.origin</code>, so framing
            it inside CinchOps would only work if we also proxied Qdrant&rsquo;s
            whole REST surface — <code className="font-mono text-xs">/collections</code>,{" "}
            <code className="font-mono text-xs">/snapshots</code> and the rest — at
            our root. That would claim a dozen generic paths and put Qdrant&rsquo;s
            unauthenticated write API on the CinchOps origin. At its own origin it
            just works, so that is where it stays. Requires the VPN.
          </p>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <a href={data.console_url} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Open the Qdrant console
            </a>
          </Button>
          <p className="mt-2 break-all font-mono text-xs text-muted-foreground">
            {data.console_url}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
